import { localize, setLocale } from '@vee-validate/i18n';
import en from '@vee-validate/i18n/dist/locale/en.json';
import fr from '@vee-validate/i18n/dist/locale/fr.json';
import { digits, email, length, max, min, numeric, regex, required, url } from '@vee-validate/rules';
import { configure, defineRule } from 'vee-validate';

import { Locale } from '@silae/composables';
import { DEFAULT_LOCALE, MINIMUM_PASSWORD_LENGTH, SECURED_USERNAME_PATTERN, SPECIAL_CHARS_PATTERN } from '~/utils';

export const updateVeeValidateConfig = (locale: Locale = DEFAULT_LOCALE) => {
	setLocale(locale.value);
};

export const setupVeeValidate = (config: Locale = DEFAULT_LOCALE) => {
	/* import rules from vee validate global validators, see: https://vee-validate.logaretm.com/v4/guide/global-validators/#vee-validaterules */
	/* to import more rules : https://vee-validate.logaretm.com/v4/guide/global-validators/#available-rules */
	defineRule('email', email);
	defineRule('digits', digits);
	defineRule('required', required);
	defineRule('min', min);
	defineRule('max', max);
	defineRule('url', url);
	defineRule('regex', regex);
	defineRule('numeric', numeric);
	defineRule('length', length);

	// Custom rules
	defineRule('username_safety_check', (value: string) => SECURED_USERNAME_PATTERN.test(value));
	defineRule('no_leading_or_trailing_spaces', (value: string) => value?.trim() === value);
	defineRule('password_strength', (value: string) => {
		const hasALowercase = /[a-z]/.test(value);
		const hasAnUppercase = /[A-Z]/.test(value);
		const hasANumber = /\d/.test(value);
		const hasASpecialChar = SPECIAL_CHARS_PATTERN.test(value);
		const hasEnoughChars = value?.length >= MINIMUM_PASSWORD_LENGTH;

		return hasALowercase && hasAnUppercase && hasANumber && hasASpecialChar && hasEnoughChars;
	});
	defineRule('password_different', (value: string, target: string) => String(value) !== String(target));
	defineRule('password_confirmed', (value: string, target: string) => String(value) === String(target));

	/* localization for error messages, see: https://vee-validate.logaretm.com/v4/guide/i18n/ */
	/* french locale messages can be found here : https://www.npmjs.com/package/@vee-validate/i18n?activeTab=code */

	configure({
		generateMessage: localize({
			en: {
				...en,
				messages: {
					...en.messages,
					password_strength: 'Password does not match required security constraints'
				}
			},
			fr: {
				...fr,
				messages: {
					...fr.messages,
					digits: 'Le champ doit être un nombre entier de 0:{length} chiffres',
					email: 'Le champ doit être une adresse e-mail valide',
					required: 'Ce champ est requis',
					max: 'Le champ ne peut pas contenir plus de 0:{length} caractères',
					min: 'Le champ doit contenir au minimum 0:{length} caractères',
					url: 'Le champ doit être une url valide',
					no_leading_or_trailing_spaces: 'Le champ ne peut pas commencer ou se terminer par des espaces',
					password_strength: 'Le mot de passe ne respecte pas les contraintes de sécurité',
					password_different: 'Le nouveau mot de passe doit être différent du mot de passe actuel',
					password_confirmed: 'La confirmation du mot de passe ne correspond pas au nouveau mot de passe'
				}
			}
		})
	});
	updateVeeValidateConfig(config);
};
